import React, { useState, useMemo } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Tooltip,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Description as DocumentIcon,
  Gavel as CaseIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  OpenInNew as OpenIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  setLoadingDocument,
  setSelectedCitation,
  setDocument,
  setDocumentSourceUrl
} from '../../../../redux/actions/documentAction';
import { backendClient } from '../../../../api/backend';
import { useSnackbar } from 'notistack';
import DocumentViewer from '../../../../components/document-viewer/DocumentViewer';
import { getCitationSectionTitle, isMobile } from '../../../../utils/utils';
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';

const SourcesModal = ({ open, onClose, sources = [] }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const dispatch = useDispatch();
  const selectedCitation = useSelector(state => state.document.selectedCitation);
  const document = useSelector(state => state.document.document);
  const isLoadingDocument = useSelector(state => state.document.isLoadingDocument);
  const documentSourceUrl = useSelector(state => state.document.documentSourceUrl);
  const { enqueueSnackbar } = useSnackbar();

  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [expandedDocId, setExpandedDocId] = useState(null);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState(null);
  const [loadingDocId, setLoadingDocId] = useState(null);

  // Group sources by document_id
  const groupedSources = useMemo(() => {
    if (!sources || sources.length === 0) return [];

    const groups = {};
    const order = [];

    sources.forEach((source) => {
      const metadata = source.metadata || {};
      const docId = source.document_id || metadata.document_id || `ungrouped-${Math.random()}`;

      if (!groups[docId]) {
        groups[docId] = {
          docId,
          metadata,
          sources: [],
        };
        order.push(docId);
      }
      groups[docId].sources.push(source);
    });

    return order.map(id => groups[id]);
  }, [sources]);

  // Resolve blob name from files object or legacy paths (same logic as Citations.js)
  const resolveBlobName = (metadata) => {
    const lang = metadata.source_language || metadata.language || 'ar';
    const files = metadata.files;

    if (files) {
      if (files.html && files.html[lang]) return files.html[lang];
      if (files.html && files.html.ar) return files.html.ar;
      if (files.html) {
        const first = Object.values(files.html)[0];
        if (first) return first;
      }
      if (files.pdf && files.pdf[lang]) return files.pdf[lang];
      if (files.pdf && files.pdf.ar) return files.pdf.ar;
      if (files.pdf) {
        const first = Object.values(files.pdf)[0];
        if (first) return first;
      }
    }

    // Fallback to legacy paths
    if (metadata.html_path_ar) return metadata.html_path_ar;
    if (metadata.html_path_en) return metadata.html_path_en;
    if (metadata.pdf_path_ar) return metadata.pdf_path_ar;
    if (metadata.pdf_path_en) return metadata.pdf_path_en;
    if (metadata.pdf_path) return metadata.pdf_path;

    return null;
  };

  // Check if a source has a viewable document file
  const hasDocumentFile = (source) => {
    return !!resolveBlobName(source.metadata || {});
  };

  // Select a source to show its content in the right panel (without loading document)
  const handleSelectSource = (source) => {
    const metadata = source.metadata || {};
    const documentId = metadata.document_id || source.document_id;

    const sectionId = source.id ? `${source.id}` : `${documentId}-${metadata.section_title || Math.random()}`
    const selectedCitation_ = {
      id: sectionId,
      metadata: metadata,
      page_content: source.page_content || source.text
    };

    dispatch(setSelectedCitation(selectedCitation_));
  };

  // Open the full document in the viewer
  const handleOpenDocument = async (source) => {
    const metadata = source.metadata || {};
    const documentId = metadata.document_id || source.document_id;

    if (!documentId) {
      console.warn('No document ID found in citation');
      return;
    }

    // Always set the selected citation
    handleSelectSource(source);

    // Check if document is already loaded
    if (document?.id === documentId) {
      if (isMobile()) setIsMobileModalOpen(true);
      return;
    }

    const blobName = resolveBlobName(metadata);
    if (!blobName) {
      // No file available — content preview is already shown via handleSelectSource
      return;
    }

    try {
      setLoadingDocId(documentId);
      dispatch(setLoadingDocument(true));

      const containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME;
      const data = await backendClient.getDocumentSas(containerName, blobName);

      if (!data || !data.blob_url_with_sas) {
        // Document not found in storage — fall back to content preview
        console.warn(`Document not found in storage: ${blobName}`);
        return;
      }

      const response = await fetch(data.blob_url_with_sas);
      if (!response.ok) {
        console.warn(`Failed to fetch document (${response.status}): ${blobName}`);
        return;
      }

      const url = response.url;
      if (!url) {
        console.warn('No URL received from document fetch');
        return;
      }

      dispatch(setDocument({
        id: documentId,
        url: url,
        language: metadata.source_language || metadata.language
      }));
      dispatch(setDocumentSourceUrl(url));

      if (isMobile()) setIsMobileModalOpen(true);
    } catch (error) {
      console.error('Error fetching or processing the document:', error);
      enqueueSnackbar(t('document_fetch_error'), { variant: 'error' });
    } finally {
      dispatch(setLoadingDocument(false));
      setLoadingDocId(null);
    }

    ReactGA.event({
      category: 'RiskAnalysis',
      action: 'Clicked Source Citation',
      label: `Document ID: ${documentId}, Section Title: ${metadata.section_title}`
    });
    tracker.event({
      category: 'RiskAnalysis',
      action: 'Clicked Source Citation',
      label: `Document ID: ${documentId}, Section Title: ${metadata.section_title}`
    });
  };

  const handleCopyContent = async (content) => {
    await navigator.clipboard.writeText(content);
  };

  const handleCloseModal = () => {
    setIsMobileModalOpen(false);
    dispatch(setSelectedCitation(null));
    onClose();
  };

  const getSourceIcon = (source) => {
    const docType = source.metadata?.doc_type || source.doc_type;
    if (docType === 'case-law') return <CaseIcon fontSize="small" />;
    return <DocumentIcon fontSize="small" />;
  };

  const getSourceTitle = (source) => {
    const metadata = source.metadata || {};
    if (metadata.doc_type === 'case-law') {
      return metadata.case_number || metadata.subject_matter || t('unknown_document');
    }
    return metadata.title || t('unknown_document');
  };


  const getDocSubtitle = (metadata) => {
    const parts = [];
    if (metadata.doc_type === 'law') {
      if (metadata.doc_type_label) {
        parts.push(metadata.doc_type_label);
      } else if (metadata.legislation_type) {
        const type = metadata.legislation_type[currentLang] || metadata.legislation_type['en'] || metadata.legislation_type['ar'];
        if (type) parts.push(type);
      }
      if (metadata.doc_number) parts.push(`No. ${metadata.doc_number}`);
      if (metadata.issue_year) parts.push(`${metadata.issue_year}`);
    } else if (metadata.doc_type === 'case-law') {
      if (metadata.court_name) parts.push(metadata.court_name);
      if (metadata.case_type) parts.push(metadata.case_type);
      if (metadata.case_year) parts.push(`${metadata.case_year}`);
    }
    return parts.join(' \u2022 ');
  };

  const getSectionLabel = (source) => {
    const metadata = source.metadata || {};
    const raw = metadata.section_title || metadata.chunk_type || '';
    return getCitationSectionTitle(raw);
  };

  const isDocSelected = (docId) => {
    const selectedDocId = selectedCitation?.metadata?.document_id || selectedCitation?.document_id;
    return selectedCitation && docId === selectedDocId;
  };

  // Mobile document viewer
  if (isMobileModalOpen) {
    return (
      <Modal
        open={isMobileModalOpen}
        onClose={() => setIsMobileModalOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          width: '90%',
          height: '80vh',
          backgroundColor: theme.palette.background.paper,
          padding: isMobile() ? '5px' : '16px',
          borderRadius: 2,
          overflow: 'auto'
        }}>
          <DocumentViewer
            document={document}
            documentSourceUrl={documentSourceUrl}
            isLoadingDocument={isLoadingDocument}
            selectedCitation={selectedCitation}
            setDocument={(doc) => dispatch(setDocument(doc))}
            showTranslationMenu={true}
          />
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box sx={{
        width: '90%',
        height: '80vh',
        backgroundColor: theme.palette.background.paper,
        padding: isMobile() ? '5px' : '16px',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {t('riskAnalysis.sources.title', 'Sources juridiques')} ({sources.length})
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Content */}
        <Box sx={{ display: 'flex', flex: 1, gap: 2, overflow: 'hidden' }}>
          {/* Left side - Sources list */}
          <Box sx={{ width: '40%', overflow: 'auto', pr: 1 }}>
            {sources.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {t('riskAnalysis.sources.noSources', 'No legal sources found for this risk.')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {groupedSources.map((group) => {
                  const { docId, metadata, sources: groupSources } = group;
                  const isExpanded = expandedDocId === docId;
                  const isSelected = isDocSelected(docId);
                  const hasSingleSection = groupSources.length === 1;

                  return (
                    <Paper
                      key={docId}
                      elevation={0}
                      sx={{
                        border: isSelected
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        ...(isSelected && {
                          boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        }),
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                        },
                      }}
                    >
                      {/* Document Header */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.5,
                          cursor: 'pointer',
                          bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
                        }}
                        onClick={() => {
                          if (expandedDocId === docId) {
                            setExpandedDocId(null);
                            setExpandedSectionIndex(null);
                          } else {
                            setExpandedDocId(docId);
                            setExpandedSectionIndex(null);
                          }
                          handleOpenDocument(groupSources[0]);
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}>
                          {getSourceIcon(groupSources[0])}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {getSourceTitle(groupSources[0])}
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: theme.palette.text.secondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}>
                            {getDocSubtitle(metadata)}
                            {!hasSingleSection && ` \u2022 ${groupSources.length} ${t('sections', 'sections')}`}
                          </Typography>
                        </Box>

                        <IconButton size="small">
                          {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </Box>

                      {/* Expanded Content */}
                      <Collapse in={isExpanded}>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                          {/* Document-level actions */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: hasSingleSection ? 0 : 1.5 }}>
                            {hasDocumentFile(groupSources[0]) && (
                              <Typography
                                component="span"
                                variant="caption"
                                onClick={() => handleOpenDocument(groupSources[0])}
                                sx={{
                                  color: theme.palette.primary.main,
                                  cursor: loadingDocId === docId ? 'wait' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {loadingDocId === docId ? (
                                  <CircularProgress size={12} color="primary" />
                                ) : (
                                  <OpenIcon sx={{ fontSize: 14 }} />
                                )}
                                {t('view_document', 'Voir le document')}
                              </Typography>
                            )}
                            {metadata.source_url && (
                              <Typography
                                component="a"
                                href={metadata.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                sx={{
                                  color: theme.palette.primary.main,
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                <OpenIcon sx={{ fontSize: 14 }} />
                                Source
                              </Typography>
                            )}
                          </Box>

                          {/* Sections */}
                          {hasSingleSection ? (
                            <Box>
                              <Typography variant="body2" sx={{
                                color: theme.palette.text.secondary,
                                mt: 1.5,
                                maxHeight: 150,
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.85rem',
                                lineHeight: 1.6,
                                bgcolor: alpha(theme.palette.grey[500], 0.05),
                                p: 1.5,
                                borderRadius: 1,
                                direction: (metadata.source_language || metadata.language) === 'ar' ? 'rtl' : 'ltr',
                              }}>
                                {groupSources[0].page_content || groupSources[0].text}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Tooltip title={t('copy', 'Copy')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopyContent(groupSources[0].page_content || groupSources[0].text || '')}
                                    sx={{
                                      color: theme.palette.text.secondary,
                                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                                    }}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {groupSources.map((source, sIdx) => {
                                const sectionKey = `${docId}-${sIdx}`;
                                const isSectionExpanded = expandedSectionIndex === sectionKey;
                                const sectionLabel = getSectionLabel(source);

                                return (
                                  <Box
                                    key={sectionKey}
                                    sx={{
                                      borderRadius: 1,
                                      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 1.5,
                                        py: 1,
                                        cursor: 'pointer',
                                        bgcolor: isSectionExpanded
                                          ? alpha(theme.palette.primary.main, 0.03)
                                          : 'transparent',
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        },
                                      }}
                                      onClick={() => {
                                        setExpandedSectionIndex(isSectionExpanded ? null : sectionKey);
                                        handleSelectSource(source);
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ flex: 1, fontWeight: 500, color: theme.palette.text.primary }}
                                      >
                                        {sectionLabel}
                                      </Typography>
                                      <Tooltip title={t('copy', 'Copy')}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyContent(source.page_content || source.text || '');
                                          }}
                                          sx={{
                                            color: theme.palette.text.secondary,
                                            p: 0.5,
                                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                                          }}
                                        >
                                          <CopyIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <IconButton size="small" sx={{ p: 0.5 }}>
                                        {isSectionExpanded ? (
                                          <CollapseIcon sx={{ fontSize: 18 }} />
                                        ) : (
                                          <ExpandIcon sx={{ fontSize: 18 }} />
                                        )}
                                      </IconButton>
                                    </Box>
                                    <Collapse in={isSectionExpanded}>
                                      <Box sx={{ px: 1.5, pb: 1.5 }}>
                                        <Typography variant="body2" sx={{
                                          color: theme.palette.text.secondary,
                                          maxHeight: 150,
                                          overflow: 'auto',
                                          whiteSpace: 'pre-wrap',
                                          fontSize: '0.85rem',
                                          lineHeight: 1.6,
                                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                                          p: 1.5,
                                          borderRadius: 1,
                                          direction: (source.metadata?.source_language || source.metadata?.language) === 'ar' ? 'rtl' : 'ltr',
                                        }}>
                                          {source.page_content || source.text}
                                        </Typography>
                                      </Box>
                                    </Collapse>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Right side - Document viewer or content preview */}
          <Box sx={{ width: '60%', overflow: 'hidden' }}>
            {isLoadingDocument ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : selectedCitation && (document?.id === selectedCitation?.metadata?.document_id) ? (
              <DocumentViewer
                document={document}
                documentSourceUrl={documentSourceUrl}
                isLoadingDocument={isLoadingDocument}
                selectedCitation={selectedCitation}
                setDocument={(doc) => dispatch(setDocument(doc))}
                showTranslationMenu={true}
              />
            ) : selectedCitation ? (
              // Content preview when no document file is loaded
              <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  {selectedCitation.metadata?.title || selectedCitation.metadata?.case_number || t('unknown_document')}
                </Typography>
                {selectedCitation.metadata?.section_title && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {getCitationSectionTitle(selectedCitation.metadata.section_title)}
                  </Typography>
                )}
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.8,
                    direction: (selectedCitation.metadata?.source_language || selectedCitation.metadata?.language) === 'ar' ? 'rtl' : 'ltr',
                  }}
                >
                  {selectedCitation.page_content}
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: 'text.secondary'
              }}>
                <Typography variant="body2">
                  {t('riskAnalysis.sources.selectSource', 'Select a source from the list to view the document')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SourcesModal;
